import { Injectable, signal } from '@angular/core';
import { check, Update } from '@tauri-apps/plugin-updater';
import { relaunch } from '@tauri-apps/plugin-process';
import { info, error } from '@tauri-apps/plugin-log';
import { confirm, message } from '@tauri-apps/plugin-dialog';

/** Version the user last said "no" to, so the prompt does not nag every launch. */
const SKIPPED_VERSION_KEY = 'updater.skippedVersion';

@Injectable({ providedIn: 'root' })
export class UpdaterService {
  // Signals pour suivre l'état
  isChecking = signal(false);
  isDownloading = signal(false);
  downloadProgress = signal(0);

  /**
   * @param force ask again even for a version the user already declined — for a
   *              check started from a menu or a button, where silence is wrong.
   */
  async checkAndUpdate(force = false): Promise<boolean> {
    this.isChecking.set(true);

    try {
      await info('[updater] Starting update check...');

      let update: Update | null = null;

      try {
        update = await check();
      } catch (checkError: any) {
        await error(`[updater] Check failed: ${checkError?.message || checkError}`);
        // Ne pas afficher d'erreur à l'utilisateur pour un simple échec de vérification
        return false;
      }

      await info(`[updater] Check result: ${update ? `v${update.version} available` : 'No update'}`);

      if (!update) {
        this.isChecking.set(false);
        return false;
      }

      // Une version déjà refusée ne redemande pas à chaque démarrage.
      if (!force && this.readSkippedVersion() === update.version) {
        await info(`[updater] v${update.version} was declined earlier, not asking again`);
        this.isChecking.set(false);
        return false;
      }

      // Demander confirmation à l'utilisateur
      const userConfirmed = await confirm(
        `A new version (${update.version}) is available!\n\nDo you want to download and install it now?\n\nThe app will restart after installation.`,
        { title: 'Update Available', kind: 'info' }
      );

      await info(`[updater] User response: ${userConfirmed ? 'YES' : 'NO'}`);

      if (!userConfirmed) {
        this.writeSkippedVersion(update.version);
        this.isChecking.set(false);
        return false;
      }

      // Accepted: a later failure should be free to prompt again.
      this.writeSkippedVersion(null);

      // Commencer le téléchargement
      this.isChecking.set(false);
      this.isDownloading.set(true);
      this.downloadProgress.set(0);

      await info('[updater] Starting download...');

      try {
        let totalSize = 0;
        let downloadedSize = 0;

        await update.downloadAndInstall((event) => {
          switch (event.event) {
            case 'Started':
              totalSize = event.data.contentLength || 0;
              info(`[updater] Download started, size: ${totalSize} bytes`);
              break;
            case 'Progress':
              downloadedSize += event.data.chunkLength;
              const progress = totalSize > 0 ? Math.round((downloadedSize / totalSize) * 100) : 0;
              this.downloadProgress.set(progress);
              // Log every 10%
              if (progress % 10 === 0) {
                info(`[updater] Download progress: ${progress}%`);
              }
              break;
            case 'Finished':
              this.downloadProgress.set(100);
              info('[updater] Download finished');
              break;
          }
        });

        await info('[updater] Installation complete, preparing to relaunch...');

        // Petit délai pour s'assurer que tout est bien écrit
        await new Promise(resolve => setTimeout(resolve, 500));

        await info('[updater] Relaunching application...');
        await relaunch();

        // Ce code ne devrait jamais être atteint
        return true;

      } catch (downloadError: any) {
        const errMsg = downloadError?.message || String(downloadError);
        await error(`[updater] Download/Install failed: ${errMsg}`);

        this.isDownloading.set(false);
        this.downloadProgress.set(0);

        // Afficher l'erreur à l'utilisateur
        await message(
          `Update failed: ${errMsg}\n\nPlease try again later or download the update manually from GitHub.`,
          { title: 'Update Error', kind: 'error' }
        );

        return false;
      }

    } catch (e: any) {
      const errMsg = e?.message || String(e);
      await error(`[updater] Unexpected error: ${errMsg}`);

      this.isChecking.set(false);
      this.isDownloading.set(false);
      this.downloadProgress.set(0);

      return false;
    }
  }

  /** localStorage can throw (private mode, blocked storage); never fail the check over it. */
  private readSkippedVersion(): string | null {
    try {
      return localStorage.getItem(SKIPPED_VERSION_KEY);
    } catch {
      return null;
    }
  }

  private writeSkippedVersion(version: string | null) {
    try {
      if (version) localStorage.setItem(SKIPPED_VERSION_KEY, version);
      else localStorage.removeItem(SKIPPED_VERSION_KEY);
    } catch {
      // Nothing to do: the prompt simply comes back next launch.
    }
  }
}
